import {
  extractAllVariationsFromProducts,
  groupProductsByProvider,
} from 'src/modules/designStudio/helpers/productSelection/ProductDataStore';
import type {
  Product,
  ProductFilter,
  Provider,
  Variation,
} from 'src/modules/designStudio/helpers/productSelection/types';

/**
 * Filtered Product Search Utils
 */

export type ProductsGroupedByProvider = {
  provider: Provider;
  products: Product[];
};

export type VariationsGroupedByProvider = {
  provider: Provider;
  variations: Variation[];
};

export type ProvidersSummary = {
  provider: Provider;
  uniqueColoredVariations: Variation[];
  allVariations: Variation[];
};

export const getProductsGroupedByProvider = (
  products: Product[],
): ProductsGroupedByProvider[] => groupProductsByProvider(products);

/**
 * @description This function takes as input an array of products, each of which contains many variations,
 * and returns a list featured variations.
 *
 * This list of featured variations is meant to try and show as many unique color and products as possible.
 * Example:
 *
 * products: [
 *     {
 *         name: '5" Siding',
 *         variations: [RED, GREEN, BLUE]
 *     },
 *     {
 *         name: '6" Siding',
 *         variations: [RED, GREEN, BLUE]
 *     },
 *     {
 *         name: '7" Siding',
 *         variations: [RED, GREEN, BLUE]
 *     }
 * ]
 *
 * const featuredVariations = createFeaturedVariationsFromProducts(products);
 * featuredVariations = [
 *     '5" Siding RED',
 *     '6" Siding GREEN',
 *     '7" Siding BLUE'
 * ];
 *
 */
export const createFeaturedVariationsFromProducts = ({
  brandProducts,
  numVariations,
}: {
  brandProducts: Product[];
  numVariations: number;
}) => {
  const featuredVariations: Variation[] = [];
  const featuredProducts = new Set<string>();
  const featuredColors = new Set<string>();

  const extractColor = (variation: Variation): string =>
    variation.color ? variation.color.hex : variation.name;

  const allVariations = extractAllVariationsFromProducts(brandProducts);

  /**
   * At this stage we will only add a variation to the featureVariations array if:
   * - the variations products is unique within featuredVariations
   * - the variations color is unique within featuredVariations
   *    - Note: The param considerColor controls whether we try and choose unique colors.
   */
  for (const variation of allVariations) {
    const color = extractColor(variation);

    if (
      !featuredProducts.has(variation.product.uuid) &&
      !featuredColors.has(color)
    ) {
      featuredProducts.add(variation.product.uuid);
      featuredColors.add(color);
      featuredVariations.push(variation);
    }

    if (featuredVariations.length === numVariations) {
      return featuredVariations;
    }
  }

  /**
   * If we have made it to this stage then featuredVariations does not have the requested length,
   * which means that are not enough unique combinations of color & product to fill featuredVariations.
   * We therefore iterate over the variations we have not already added to featuredVariations, and add
   * them if the product or color are unique.
   */
  for (const variation of allVariations) {
    if (featuredVariations.includes(variation)) {
      // eslint-disable-next-line no-continue
      continue;
    }

    const color = extractColor(variation);

    if (
      !featuredProducts.has(variation.product.uuid) ||
      !featuredColors.has(color)
    ) {
      featuredProducts.add(variation.product.uuid);
      featuredColors.add(color);
      featuredVariations.push(variation);
    }

    if (featuredVariations.length === numVariations) {
      return featuredVariations;
    }
  }

  /**
   * If we have made it to this stage there is not enough variability within the available variations to avoid showing
   * duplicate colors or products. At this point we simply add whatever variation is available to fill out
   * featuredVariations to the requested length.
   * Note: At this point featuredVariations will include duplicate colors and products.
   */
  for (const variation of allVariations) {
    if (featuredVariations.includes(variation)) {
      // eslint-disable-next-line no-continue
      continue;
    }

    featuredVariations.push(variation);

    if (featuredVariations.length === numVariations) {
      return featuredVariations;
    }
  }

  return featuredVariations;
};

export const extractProviderSummariesWithUniqueProducts = (
  products: Product[],
): ProvidersSummary[] => {
  const productsGroupedByProvider = getProductsGroupedByProvider(products);

  return productsGroupedByProvider
    .map(group => ({
      provider: group.provider,
      allVariations: extractAllVariationsFromProducts(group.products),
      uniqueColoredVariations: createFeaturedVariationsFromProducts({
        brandProducts: group.products,
        numVariations: 6,
      }),
    }))
    .filter(summary => summary.allVariations.length !== 0);
};

export const getNumActiveFieldsInFilter = (filter: ProductFilter): number => {
  let num = 1;
  if (filter.colorGroups.length > 0) {
    num += 1;
  }
  if (filter.providers.length > 0) {
    num += 1;
  }
  if (filter.styles.length > 0) {
    num += 1;
  }
  if (filter.textures.length > 0) {
    num += 1;
  }
  if (filter.sizes.length > 0) {
    num += 1;
  }
  return num;
};
